import _debounce from 'lodash/debounce';
import { useCallback, useEffect, useState } from 'react';
import useLatest from './useLatest';

export const loadApiErr =
  '> 💡use-mapbox-search: Mapbox access token must be configured.';

function extractAddressComponents(context) {
  let city = null;
  let region = null;
  let postal = null;
  let country = null;

  if (!context) {
    return { city, region, postal, country };
  }

  for (const item of context) {
    const id = item.id.split('.')[0];
    if (id === 'place' && !city) {
      city = item.text;
    } else if (id === 'region') {
      region = item.short_code?.split('-')[1] || item.text;
    } else if (id === 'postcode') {
      postal = item.text;
    } else if (id === 'country') {
      country = item.short_code;
    }
  }

  return { city, region, postal, country };
}

function formatMapboxText(placeName, text, placeType) {
  // Check if it's a postcode - placeType can be an array or string
  const placeTypeArray = Array.isArray(placeType) ? placeType : [placeType];
  const isPostcode =
    placeTypeArray.some((type) => type === 'postcode') ||
    placeType === 'postcode';

  if (isPostcode && placeName) {
    // For postcodes, extract city name from place_name
    // Format: "City, State ZIP, Country"
    const parts = placeName.split(',');
    if (parts.length >= 2) {
      return {
        mainText: parts[0].trim(),
        secondaryText: parts.slice(1).join(',').trim(),
      };
    }
    return {
      mainText: text || placeName,
      secondaryText: '',
    };
  }

  if (text && placeName && text !== placeName) {
    // For other types, use text as main, rest as secondary
    // Only if text is different from placeName (to avoid duplicates)
    return {
      mainText: text,
      secondaryText: placeName.split(',').slice(1).join(',').trim(),
    };
  }

  if (placeName) {
    // Fallback: split place_name
    const parts = placeName.split(',');
    return {
      mainText: parts[0]?.trim() || '',
      secondaryText: parts.slice(1).join(',').trim(),
    };
  }

  return {
    mainText: text || '',
    secondaryText: '',
  };
}

function useMapboxSearch({ requestOptions, debounce = 200 }) {
  const [ready, setReady] = useState(false);
  const [value, setVal] = useState('');
  const [suggestions, setSuggestions] = useState({
    loading: false,
    status: '',
    data: [],
  });
  const requestOptionsRef = useLatest(requestOptions);
  const accessToken = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN;

  useEffect(() => {
    if (accessToken) {
      setReady(true);
    } else {
      console.error(loadApiErr);
    }
  }, []);

  const clearSuggestions = useCallback(() => {
    setSuggestions({ loading: false, status: '', data: [] });
  }, []);

  const fetchSuggestions = useCallback(
    _debounce(async (val) => {
      if (!val) {
        clearSuggestions();
        return;
      }

      if (!accessToken) {
        clearSuggestions();
        return;
      }

      // To keep the previous suggestions
      setSuggestions((prevState) => ({ ...prevState, loading: true }));

      try {
        const options = requestOptionsRef.current || {};
        const params = new URLSearchParams({
          access_token: accessToken,
          q: val,
          limit: options.limit || 5,
          autocomplete: 'true',
          ...(options.countries && { country: options.countries.join(',') }),
          ...(options.proximity && {
            proximity: Array.isArray(options.proximity)
              ? options.proximity.join(',')
              : options.proximity,
          }),
          ...(options.types && { types: options.types.join(',') }),
          ...(options.language && { language: options.language.join(',') }),
        });

        const response = await fetch(
          `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(
            val,
          )}.json?${params.toString()}`,
        );

        if (!response.ok) {
          throw new Error(`Mapbox API error: ${response.status}`);
        }

        const data = await response.json();

        // Transform Mapbox response to match expected format
        const transformedData = (data.features || [])
          .map((feature) => {
            if (!feature?.geometry) {
              return null;
            }

            const [lng, lat] = feature.geometry.coordinates;

            // Mapbox properties are directly on feature.properties
            // Handle case where properties might be undefined
            const properties = feature.properties || {};
            const place_name = properties.place_name || feature.place_name;
            const text = properties.text || feature.text;
            const place_type = properties.place_type || feature.place_type;
            const context = properties.context || feature.context;

            // Extract address components from context
            const { city, region, postal, country } =
              extractAddressComponents(context);

            // Extract main text and secondary text
            const placeName = place_name || text || '';
            const formatted = formatMapboxText(placeName, text, place_type);
            const { mainText, secondaryText } = formatted;

            // Ensure we always have valid text values
            // Check if mainText exists and has content after trimming
            const trimmedMainText = mainText?.trim();
            const trimmedSecondaryText = secondaryText?.trim();

            const finalMainText =
              trimmedMainText || placeName || text || 'Location';
            const finalSecondaryText = trimmedSecondaryText || '';

            return {
              place_id: feature.id,
              description: placeName || text || '',
              structured_formatting: {
                main_text: finalMainText,
                secondary_text: finalSecondaryText,
              },
              geometry: {
                location: {
                  lat: () => lat,
                  lng: () => lng,
                },
              },
              // Mapbox-specific data for direct coordinate access
              coordinates: [lat, lng],
              address_components: context || [],
              formatted_address: placeName,
              place_type,
              city,
              region,
              postal,
              country,
              // Keep original feature for any future use
              _original: feature,
            };
          })
          .filter(Boolean);

        setSuggestions({
          loading: false,
          status: transformedData.length > 0 ? 'OK' : 'ZERO_RESULTS',
          data: transformedData,
        });
      } catch (error) {
        console.error('Error fetching Mapbox suggestions:', error);
        setSuggestions({
          loading: false,
          status: 'ERROR',
          data: [],
        });
      }
    }, debounce),
    [],
  );

  const setValue = useCallback(
    (val, shouldFetchData = true) => {
      setVal(val);
      if (shouldFetchData) {
        fetchSuggestions(val);
      }
    },
    [fetchSuggestions],
  );

  return { ready, value, suggestions, setValue, clearSuggestions };
}

export default useMapboxSearch;
